import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaListOl,
  FaLink,
  FaUnlink,
  FaUndo,
  FaRedo,
  FaCode,
  FaEye,
  FaMagic,
  FaEraser,
  FaTrashAlt,
} from "react-icons/fa";
import "./RichTextEditor.css";

const DEFAULT_SPECIES_TEMPLATE = `<ul>
  <li><strong>Scientific Name:</strong> <em>Enter scientific name</em></li>
  <li><strong>Family:</strong> Enter family name</li>
  <li><strong>Common Name:</strong> Enter common name</li>
  <li><strong>Typical Height:</strong> 15–25 m</li>
  <li><strong>Growth:</strong> Fast-growing</li>
  <li><strong>Water / Drought Tolerance:</strong> High drought tolerance</li>
  <li><strong>Traditional Uses:</strong> Ayurvedic medicine; bark, leaves, seeds</li>
  <li><strong>Special Purpose:</strong> Agroforestry, shade, avenue planting</li>
  <li><strong>Carbon Sequestration:</strong> High carbon-storage potential</li>
  <li><strong>Climate Resilience:</strong> Suitable for dry conditions</li>
  <li><strong>Livelihoods:</strong> Timber, fuelwood, herbal products</li>
  <li><strong>Ecosystem Restoration:</strong> Suitable for afforestation and degraded-land restoration</li>
</ul>`;

const DEFAULT_REFERENCES_TEMPLATE = `<ul>
  <li><a href="https://en.wikipedia.org" target="_blank" rel="noopener noreferrer"><strong>Flora & Botanical Database:</strong> Species Information & Ecology</a></li>
  <li><a href="https://www.iucnredlist.org" target="_blank" rel="noopener noreferrer"><strong>IUCN Red List:</strong> Conservation Status</a></li>
  <li><a href="https://powo.science.kew.org" target="_blank" rel="noopener noreferrer"><strong>Plants of the World Online (Kew):</strong> Taxonomy & Distribution</a></li>
</ul>`;

const RichTextEditor = ({
  value = "",
  onChange,
  label = "Characteristics",
  placeholder = "Enter text...",
  error,
  touched,
  showTemplateBtn = true,
  templateType = "characteristics",
  minHeight = "180px",
}) => {
  const editorRef = useRef(null);
  const isFocusedRef = useRef(false);
  const lastHtmlRef = useRef("");
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlValue, setHtmlValue] = useState(value || "");
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    insertUnorderedList: false,
    insertOrderedList: false,
  });

  const updateActiveStates = useCallback(() => {
    if (isHtmlMode || !editorRef.current) return;
    try {
      setActiveFormats({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        insertUnorderedList: document.queryCommandState("insertUnorderedList"),
        insertOrderedList: document.queryCommandState("insertOrderedList"),
      });
    } catch (e) {
      // Ignore queryCommandState errors in unsupported environments
    }
  }, [isHtmlMode]);

  // Synchronize external value changes (initial load, editing population, form reset)
  useEffect(() => {
    const currentVal = value || "";
    setHtmlValue(currentVal);

    if (editorRef.current && !isHtmlMode) {
      if (!isFocusedRef.current || editorRef.current.innerHTML !== currentVal) {
        if (editorRef.current.innerHTML !== currentVal) {
          editorRef.current.innerHTML = currentVal;
          lastHtmlRef.current = currentVal;
        }
      }
    }
  }, [value, isHtmlMode]);

  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      lastHtmlRef.current = content;
      setHtmlValue(content);
      if (onChange) {
        onChange(content);
      }
      updateActiveStates();
    }
  };

  const handleRawHtmlChange = (e) => {
    const raw = e.target.value;
    lastHtmlRef.current = raw;
    setHtmlValue(raw);
    if (onChange) {
      onChange(raw);
    }
  };

  // Crucial: onMouseDown with e.preventDefault() keeps the editor focused and selection intact!
  const executeCmd = (command, val = null) => {
    if (isHtmlMode) return;
    if (editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false, val);
    handleInput();
    updateActiveStates();
  };

  const handleCreateLink = () => {
    if (isHtmlMode) return;
    const url = window.prompt("Enter web link URL (e.g. https://...):", "https://");
    if (url && url.trim() && url !== "https://") {
      executeCmd("createLink", url.trim());
      if (editorRef.current) {
        const anchors = editorRef.current.querySelectorAll("a");
        anchors.forEach((a) => {
          if (!a.getAttribute("target")) {
            a.setAttribute("target", "_blank");
            a.setAttribute("rel", "noopener noreferrer");
          }
        });
        handleInput();
      }
    }
  };

  const handleUnlink = () => {
    executeCmd("unlink");
  };

  const insertTemplate = () => {
    const chosenTemplate =
      templateType === "references"
        ? DEFAULT_REFERENCES_TEMPLATE
        : DEFAULT_SPECIES_TEMPLATE;

    const newContent = htmlValue
      ? `${htmlValue}\n${chosenTemplate}`
      : chosenTemplate;

    lastHtmlRef.current = newContent;
    setHtmlValue(newContent);
    if (onChange) {
      onChange(newContent);
    }
    if (editorRef.current && !isHtmlMode) {
      editorRef.current.innerHTML = newContent;
      editorRef.current.focus();
    }
  };

  const clearFormatting = () => {
    executeCmd("removeFormat");
  };

  const clearAll = () => {
    lastHtmlRef.current = "";
    setHtmlValue("");
    if (onChange) {
      onChange("");
    }
    if (editorRef.current && !isHtmlMode) {
      editorRef.current.innerHTML = "";
      editorRef.current.focus();
    }
    updateActiveStates();
  };

  return (
    <div style={{ width: "100%", marginBottom: "8px" }}>
      {label && (
        <label
          style={{
            fontSize: "13px",
            fontWeight: 600,
            color: "#334155",
            marginBottom: "4px",
            display: "block",
          }}
        >
          {label}
        </label>
      )}

      <div
        className={`aranya-editor-container ${
          touched && error ? "has-error" : ""
        }`}
      >
        {/* Toolbar */}
        <div className="aranya-editor-toolbar">
          {/* Undo / Redo */}
          <button
            type="button"
            className="aranya-editor-btn"
            title="Undo (Ctrl+Z)"
            onMouseDown={(e) => {
              e.preventDefault();
              executeCmd("undo");
            }}
            disabled={isHtmlMode}
          >
            <FaUndo size={11} />
          </button>

          <button
            type="button"
            className="aranya-editor-btn"
            title="Redo (Ctrl+Y)"
            onMouseDown={(e) => {
              e.preventDefault();
              executeCmd("redo");
            }}
            disabled={isHtmlMode}
          >
            <FaRedo size={11} />
          </button>

          <div className="aranya-editor-divider" />

          {/* Bold */}
          <button
            type="button"
            className={`aranya-editor-btn ${
              activeFormats.bold ? "active" : ""
            }`}
            title="Bold (Ctrl+B)"
            onMouseDown={(e) => {
              e.preventDefault();
              executeCmd("bold");
            }}
            disabled={isHtmlMode}
          >
            <FaBold size={12} />
          </button>

          {/* Italic */}
          <button
            type="button"
            className={`aranya-editor-btn ${
              activeFormats.italic ? "active" : ""
            }`}
            title="Italic (Ctrl+I)"
            onMouseDown={(e) => {
              e.preventDefault();
              executeCmd("italic");
            }}
            disabled={isHtmlMode}
          >
            <FaItalic size={12} />
          </button>

          {/* Underline */}
          <button
            type="button"
            className={`aranya-editor-btn ${
              activeFormats.underline ? "active" : ""
            }`}
            title="Underline (Ctrl+U)"
            onMouseDown={(e) => {
              e.preventDefault();
              executeCmd("underline");
            }}
            disabled={isHtmlMode}
          >
            <FaUnderline size={12} />
          </button>

          <div className="aranya-editor-divider" />

          {/* Link / Unlink */}
          <button
            type="button"
            className="aranya-editor-btn"
            title="Insert Link (href)"
            onMouseDown={(e) => {
              e.preventDefault();
              handleCreateLink();
            }}
            disabled={isHtmlMode}
          >
            <FaLink size={12} />
          </button>

          <button
            type="button"
            className="aranya-editor-btn"
            title="Remove Link"
            onMouseDown={(e) => {
              e.preventDefault();
              handleUnlink();
            }}
            disabled={isHtmlMode}
          >
            <FaUnlink size={12} />
          </button>

          <div className="aranya-editor-divider" />

          {/* Bullet List */}
          <button
            type="button"
            className={`aranya-editor-btn ${
              activeFormats.insertUnorderedList ? "active" : ""
            }`}
            title="Bullet List"
            onMouseDown={(e) => {
              e.preventDefault();
              executeCmd("insertUnorderedList");
            }}
            disabled={isHtmlMode}
          >
            <FaListUl size={12} />
          </button>

          {/* Numbered List */}
          <button
            type="button"
            className={`aranya-editor-btn ${
              activeFormats.insertOrderedList ? "active" : ""
            }`}
            title="Numbered List"
            onMouseDown={(e) => {
              e.preventDefault();
              executeCmd("insertOrderedList");
            }}
            disabled={isHtmlMode}
          >
            <FaListOl size={12} />
          </button>

          {/* Clear Formatting */}
          <button
            type="button"
            className="aranya-editor-btn"
            title="Clear Formatting Selection"
            onMouseDown={(e) => {
              e.preventDefault();
              clearFormatting();
            }}
            disabled={isHtmlMode}
          >
            <FaEraser size={12} />
          </button>

          {/* Clear All */}
          <button
            type="button"
            className="aranya-editor-btn"
            title="Clear All Text"
            style={{ color: "#dc2626" }}
            onMouseDown={(e) => {
              e.preventDefault();
              clearAll();
            }}
          >
            <FaTrashAlt size={11} /> Clear All
          </button>

          <div className="aranya-editor-divider" />

          {/* HTML Toggle */}
          <button
            type="button"
            className={`aranya-editor-btn ${isHtmlMode ? "active" : ""}`}
            title={
              isHtmlMode
                ? "Switch to Visual Editor"
                : "View / Edit Raw HTML"
            }
            onClick={() => setIsHtmlMode(!isHtmlMode)}
          >
            {isHtmlMode ? (
              <>
                <FaEye size={12} /> Visual
              </>
            ) : (
              <>
                <FaCode size={12} /> HTML
              </>
            )}
          </button>

          {/* Insert Template */}
          {showTemplateBtn && (
            <button
              type="button"
              className="aranya-editor-btn aranya-editor-btn-template"
              title={`Insert ${templateType === 'references' ? 'References' : 'Characteristics'} Template`}
              onMouseDown={(e) => {
                e.preventDefault();
                insertTemplate();
              }}
            >
              <FaMagic size={12} /> Insert Template
            </button>
          )}
        </div>

        {/* Content Box */}
        {isHtmlMode ? (
          <textarea
            className="aranya-editor-raw"
            value={htmlValue}
            onChange={handleRawHtmlChange}
            placeholder="Enter raw HTML code here..."
            rows={8}
            style={{ minHeight }}
          />
        ) : (
          <div
            ref={editorRef}
            className="aranya-editor-content"
            contentEditable
            onInput={handleInput}
            onKeyUp={updateActiveStates}
            onMouseUp={updateActiveStates}
            onFocus={() => {
              isFocusedRef.current = true;
              updateActiveStates();
            }}
            onBlur={() => {
              isFocusedRef.current = false;
              handleInput();
            }}
            data-placeholder={placeholder}
            style={{ minHeight }}
          />
        )}
      </div>

      {touched && error && (
        <div className="aranya-editor-error">{error}</div>
      )}
    </div>
  );
};

export default RichTextEditor;
